import { useAuthentication } from '@ecosystem/newron-client-lib';
import { elements } from '@ecosystem/newron-design-system';
import React, { useEffect, useState } from 'react';

import './TargetedUpsellMessage.css';

import { TargetedUpsellBody, TargetedUpsellHeading } from '../../common/constants';
import { RetailKADMessageResponse } from '../../services/Market.service';
import { ClientUserGroup, getUserGroupNamesForUser } from '../../services/Persona.service';
import { splitStatisticFromDescription } from '../../utils/statisticSplitter';
import { UpsellMessageType } from '../targeted-upsell/TargetedUpsell';

import { UpsellQualtricsWrapper } from './upsell-qualtrics-wrapper/UpsellQualtricsWrapper';

type TargetedUpsellMessageProps = {
  showUpsellModal?: boolean;
  showRetailUpsellModal?: boolean;
  onClose?: () => void;
  onTellMeMore?: () => void;
  upsellMessageForSlide?: UpsellMessageType;
  retailKADUpsellMessage?: RetailKADMessageResponse;
  setShowUpsellModal?: (show: boolean) => void;
  setShowRetailUpsellModal?: (show: boolean) => void;
};

export interface TargetedUpsellContents {
  header: string;
  statistic?: string;
  statisticDescription?: string;
  body: string;
}

export const TargetedUpsellMessage = ({
  showUpsellModal,
  showRetailUpsellModal,
  onClose,
  upsellMessageForSlide,
  retailKADUpsellMessage,
  setShowUpsellModal,
  setShowRetailUpsellModal,
}: TargetedUpsellMessageProps): JSX.Element => {
  const [clientUserGroups, setClientUserGroups] = useState<ClientUserGroup>();
  const { Icon } = elements;

  const { getAccessToken } = useAuthentication();

  useEffect(() => {
    (async () => {
      const token = await getAccessToken();
      const fetchedClientData = await getUserGroupNamesForUser(token);
      setClientUserGroups(fetchedClientData);
    })();
  }, [getAccessToken]);

  if (!upsellMessageForSlide?.upsellMessage && !retailKADUpsellMessage) {
    return null;
  }

  const upsellBodyContent = (): TargetedUpsellContents => {
    if (retailKADUpsellMessage) {
      const { statistic, description } = splitStatisticFromDescription(retailKADUpsellMessage.statement2);
      return {
        header: retailKADUpsellMessage.statement1,
        statistic,
        statisticDescription: description,
        body: retailKADUpsellMessage.statement3.replace('...', ''),
      };
    } else {
      if (upsellMessageForSlide?.condition1 && upsellMessageForSlide?.condition2) {
        return {
          header: `${TargetedUpsellHeading.bothConditions} ${upsellMessageForSlide?.dataPeriodDisplay}`,
          body: TargetedUpsellBody.bothConditions,
        };
      } else if (upsellMessageForSlide?.condition1) {
        return {
          header: `${TargetedUpsellHeading.condition1} ${upsellMessageForSlide?.dataPeriodDisplay}`,
          body: TargetedUpsellBody.condition1,
        };
      } else if (upsellMessageForSlide?.condition2) {
        return {
          header: `${TargetedUpsellHeading.condition2} ${upsellMessageForSlide?.dataPeriodDisplay}`,
          body: TargetedUpsellBody.condition2,
        };
      }
    }
  };

  const upsellContent = upsellBodyContent();

  return (
    (showUpsellModal || showRetailUpsellModal) && (
      <div
        className="Root-mfe-upsell-tooltip upsell-4x-6x-slides-message fixed pt-4 pb-6"
        data-test-id="upsell-4x-6x-slides-message"
      >
        <div className="flex justify-end px-4">
          <button
            onClick={() => {
              onClose && onClose();
            }}
            data-test-id="close-info-box"
          >
            <Icon color="brand">
              <Icon.icons.Close />
            </Icon>
          </button>
        </div>
        <div className={`px-6 targeted-header ${showRetailUpsellModal ? 'retail-upsell-header' : ''}`}>
          {upsellContent?.header}
        </div>
        {showRetailUpsellModal && (
          <div className="relative">
            <div className="px-6 retail-kad-stat-header">
              <div className="mt-4 retail-kad-stats">{upsellContent?.statistic}</div>
              <div className="retail-kad-sub-title">{upsellContent?.statisticDescription.toLowerCase()}</div>
            </div>
          </div>
        )}
        <div className="relative">
          <div className="px-6">
            <p>{upsellMessageForSlide?.upsellMessage}</p>
            <div className="mt-4 retail-kad-text">{upsellContent?.body}</div>
          </div>
        </div>
        <div className="flex mt-6 btn-wrapper">
          <UpsellQualtricsWrapper
            setShowUpsellModal={setShowUpsellModal}
            setShowRetailUpsellModal={setShowRetailUpsellModal}
            upsellMessageForSlide={upsellMessageForSlide}
            retailKADUpsellMessage={retailKADUpsellMessage}
            clientUserGroups={clientUserGroups}
          />
        </div>
      </div>
    )
  );
};
